import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { LogoIcon } from '@/components/ui/Logo';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle } from 'lucide-react';
import { BrandBar } from '@/components/ui/BrandLoader';

type Status = 'loading' | 'success' | 'error';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Read token from query string: /verify-email?token=...
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMessage(t('auth:verifyEmail.noToken'));
      setStatus('error');
      return;
    }

    let cancelled = false;

    api
      .post('/auth/verify-email', { token })
      .then(() => {
        if (!cancelled) setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : '';
        setErrorMessage(msg || t('auth:verifyEmail.errorDesc'));
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen w-full flex bg-[var(--color-base)] text-[var(--color-text)]">
      {/* ── Left: content pane ── */}
      <div className="relative w-full lg:w-[480px] flex flex-col p-8 lg:p-12 z-10 bg-[var(--color-base)] shadow-[20px_0_40px_rgba(0,0,0,0.3)]">
        {/* Brand pinned top */}
        <button
          onClick={() => navigate({ to: '/' })}
          className="flex items-center gap-1.5 self-start"
          aria-label={t('a11y:backHome')}
        >
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>

        {/* Content vertically centered */}
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            className="text-center"
          >
            {status === 'loading' && (
              <>
                <div className="flex justify-center mb-6">
                  <BrandBar />
                </div>
                <h1 className="text-2xl font-semibold mb-2 tracking-tight">
                  {t('auth:verifyEmail.title')}
                </h1>
                <p className="text-[var(--color-text-muted)]">
                  {t('auth:verifyEmail.verifying')}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
                    <CheckCircle className="h-8 w-8" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold mb-3 tracking-tight">
                  {t('auth:verifyEmail.success')}
                </h1>
                <p className="text-[var(--color-text-muted)] mb-8 max-w-sm mx-auto">
                  {t('auth:verifyEmail.successDesc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    variant="primary"
                    onClick={() => navigate({ to: '/dashboard' })}
                  >
                    {t('auth:verifyEmail.goToDashboard')}
                  </Button>
                  <Link
                    to="/login"
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                  >
                    {t('auth:verifyEmail.goToLogin')}
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]">
                    <XCircle className="h-8 w-8" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold mb-3 tracking-tight">
                  {t('auth:verifyEmail.errorTitle')}
                </h1>
                <p className="text-[var(--color-text-muted)] mb-2 max-w-sm mx-auto">
                  {errorMessage || t('auth:verifyEmail.errorDesc')}
                </p>
                <p className="text-[var(--color-text-muted)] mb-8 max-w-sm mx-auto text-sm">
                  {t('auth:verifyEmail.errorDesc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    variant="primary"
                    onClick={() => navigate({ to: '/login' })}
                  >
                    {t('auth:verifyEmail.goToLogin')}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* ── Right: ambient pane ── */}
      <div className="hidden lg:block relative flex-1 bg-[var(--color-surface)] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-30 animate-[pulse_10s_ease-in-out_infinite]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--color-base)] opacity-50" />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full pointer-events-none"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 150, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[var(--color-primary)]/10 rounded-full pointer-events-none"
        />
      </div>
    </div>
  );
}
